import { isStringNullOrWhiteSpace } from 'owa-localize';
import getPersonaPhotoQueue from './getPersonaPhotoQueue';
import setIsPendingFetch from './setIsPendingFetch';
import { personaPhotoEntries } from '../store/localStore';
import PersonaControlViewState from '../store/schema/PersonaControlViewState';
import getImageUrl from '../utils/getImageUrl';
import getPersonaPhotoUrl from '../utils/getPersonaPhotoUrl';
import { logVerboseUsage, VerboseDatapoint } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { HttpReadyState, HttpStatusCode } from 'owa-http-status-codes';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

import { getDelegationTokenForOwa } from 'owa-tokenprovider';
import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';

const MIN_BLOB_SIZE: number = 100;
const GET_PROFILE_IMAGE_TIME = 'GetProfileImageTime';
const GET_PERSONA_TIME = 'GetPersonaTime';
const GET_PROFILE_IMAGE_NO_PHOTO = 'GetProfileImageNoPhoto';
const GET_PERSONA_NO_PHOTO = 'GetPersonaResponseNoPhoto';
const IAPI_TOKEN_SCOPE: string = 'https://outlook.office.com/M365.Access';

/**
 * Downloads the persona photo
 * @param personaViewState the view state for the persona
 * @param forceFetch If true, downloads the photo using Cache-Control:no-cache header to avoid browser cache
 */
export default async function downloadPersonaPhoto(
    personaViewState: PersonaControlViewState,
    forceFetch?: boolean
) {
    const personaBlob = personaViewState.personaBlob;

    if (!forceFetch) {
        // Do not proceed if
        // 1. The request is pending
        // 2. The request had failed (some users don't have photos).
        // 3. Browser has cached photo for it.
        // 4. Email is undefined/null or empty
        if (
            personaBlob.isPendingFetch ||
            personaBlob.hasFetchFailed ||
            (personaBlob.blobUrl && personaPhotoEntries[personaBlob.blobUrl]) ||
            (isStringNullOrWhiteSpace(personaViewState.emailAddress) &&
                isStringNullOrWhiteSpace(personaViewState.personaId))
        ) {
            return;
        }
    }

    await new Promise(async (resolve, reject) => {
        const request =
            isConsumer() && isFeatureEnabled('rp-imageApiConsumer')
                ? await getProfileImageRequest(personaViewState, forceFetch)
                : getPersonaPhotoRequest(personaViewState, forceFetch);

        if (request) {
            // set isPendingFetch to true now that we are downloading the persona
            setIsPendingFetch(personaViewState, true /* isPendingFetch */);
            let getPersonaDatapoint = getDatapoint();
            request.onreadystatechange = function() {
                if (request.readyState === HttpReadyState.Done) {
                    processGetPhotoResponse(personaViewState, request);
                    getPersonaDatapoint.end();
                    getPersonaDatapoint = null;
                    resolve();
                }
            };

            try {
                request.send();
            } catch (error) {
                setIsPendingFetch(personaViewState, false /* isPendingFetch */);

                // We see [The download of the specified resource has failed.] expcetion for some personas in IE.
                // Till we figure out the root cause we need to catch this exception
                // VSO 17536 - https://outlookweb.visualstudio.com/Outlook%20Web/_workitems?id=17536
                trace.warn(`Error in getPersonaPhoto ${error}`);
                reject(error);
            }
        } else {
            reject('request is null');
        }
    });
}

function getDatapoint(): VerboseDatapoint {
    const datapointName =
        isConsumer() && isFeatureEnabled('rp-imageApiConsumer')
            ? GET_PROFILE_IMAGE_TIME
            : GET_PERSONA_TIME;
    return new VerboseDatapoint(datapointName);
}

/**
 * Gets the getPersonaPhoto request
 * @param personaViewState the view state for the persona control
 * @param forceFetch If true, downloads the photo using Cache-Control:no-cache header to avoid browser cache
 */
function getPersonaPhotoRequest(
    personaViewState: PersonaControlViewState,
    forceFetch?: boolean
): XMLHttpRequest {
    const photoUrl = getPersonaPhotoUrl(personaViewState.emailAddress, personaViewState.personaId);

    // VSO - 18388 - Use fetch() instead of XHR call for getPersonaPhoto
    const request = new XMLHttpRequest();
    request.open('GET', photoUrl);
    request.responseType = 'blob';

    if (forceFetch) {
        request.setRequestHeader('Cache-Control', 'no-cache');
    }

    return request;
}

/**
 * Gets the profile image request
 * @param personaViewState the view state for the persona control
 * @param forceFetch If true, downloads the photo using Cache-Control:no-cache header to avoid browser cache
 */
async function getProfileImageRequest(
    personaViewState: PersonaControlViewState,
    forceFetch: boolean
): Promise<XMLHttpRequest> {
    const token = await getDelegationTokenForOwa(IAPI_TOKEN_SCOPE);

    if (token) {
        const photoUrl = getImageUrl(personaViewState.emailAddress, personaViewState.mailboxType);

        const request = new XMLHttpRequest();
        request.open('GET', photoUrl);
        request.responseType = 'blob';

        if (forceFetch) {
            request.setRequestHeader('Cache-Control', 'no-cache');
        }

        request.setRequestHeader('Content-Type', 'image/gif');

        request.setRequestHeader('Authorization', `Bearer ${token}`);

        // Set the anchor mailbox to the target mailbox
        request.setRequestHeader('X-AnchorMailbox', personaViewState.emailAddress);

        return request;
    }

    // VSO 45906: token may return null for the first few requests
    // If the token has not returned, re-queue this request
    const { name, emailAddress, personaId, size, mailboxType } = personaViewState;
    getPersonaPhotoQueue.add({ name, emailAddress, personaId, size, mailboxType });
    return null;
}

/**
 * Processes the XHR response from getPersonaPhoto
 * @param personaViewState the view state for the persona control
 * @param getPersonaRequest the XHR request for the get persona
 */
let processGetPhotoResponse = action('processGetPhotoResponse')(function processGetPhotoResponse(
    personaViewState: PersonaControlViewState,
    getPersonaRequest: XMLHttpRequest
) {
    // VSO - 183944 - track requests that are taking longer and the time
    setIsPendingFetch(personaViewState, false /* isPendingFetch */);
    if (getPersonaRequest.status === HttpStatusCode.OK) {
        const blob = getPersonaRequest.response;
        const contentType = getPersonaRequest.getResponseHeader('Content-Type');
        const isBlob =
            contentType && contentType.toLowerCase().indexOf('image') == 0 && blob instanceof Blob;

        if (isBlob) {
            if (blob.size > MIN_BLOB_SIZE) {
                // Create URL for the image data blob
                personaViewState.personaBlob.blobUrl = URL.createObjectURL(blob);

                // Bookkeeping all the persona photos browser has cached.
                personaPhotoEntries[personaViewState.personaBlob.blobUrl] = true;
                personaViewState.personaBlob.hasFetchFailed = false;
                return;
            } else {
                logNoPhoto();
            }
        }
    }

    personaViewState.personaBlob.hasFetchFailed = true;
});

function logNoPhoto(): void {
    const useImageApi = isConsumer() && isFeatureEnabled('rp-imageApiConsumer');
    const eventName = useImageApi ? GET_PROFILE_IMAGE_NO_PHOTO : GET_PERSONA_NO_PHOTO;

    logVerboseUsage(eventName);
}
